import type { layout } from "@app/domain";
import { ROOM_WIDTHS, type Scene, srcSet, TABLE_WIDTHS } from "@app/ui";
import { useState } from "react";

/** The room fills the viewport: the scene picture over its ground colour, a vignette on top (design). */
export function Room({ scene }: { scene: Scene }) {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 overflow-hidden"
      style={{ background: scene.ground }}
    >
      <img
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-90"
        decoding="async"
        sizes="100vw"
        src={`${scene.room}-${ROOM_WIDTHS[0]}.webp`}
        srcSet={srcSet(scene.room, ROOM_WIDTHS)}
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle,transparent_35%,var(--p-ground)_100%)]" />
    </div>
  );
}

/**
 * The table picture at the canvas surface. The picture is landscape; on a turned
 * (portrait) canvas it is drawn at the surface's swapped size and turned a quarter
 * about its centre. A plain felt oval stands in if it fails (review 0012 U1).
 */
export function TableArt({
  scene,
  rect,
  turned,
}: {
  scene: Scene;
  rect: layout.Rect;
  turned: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const w = turned ? rect.h : rect.w;
  const h = turned ? rect.w : rect.h;
  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute"
        style={{
          left: rect.x + (rect.w - w) / 2,
          top: rect.y + (rect.h - h) / 2,
          width: w,
          height: h,
          transform: turned ? "rotate(90deg)" : undefined,
        }}
      >
        {failed ? (
          <span
            className="absolute inset-x-[6%] inset-y-[14%] rounded-full border-[12px] border-edge"
            style={{ background: scene.ground }}
          />
        ) : (
          <img
            alt=""
            className="h-full w-full object-contain drop-shadow-[0_30px_40px_rgb(0_0_0/0.6)]"
            decoding="async"
            sizes={`${Math.round(w)}px`}
            src={`${scene.table}-${TABLE_WIDTHS[0]}.webp`}
            srcSet={srcSet(scene.table, TABLE_WIDTHS)}
            onError={() => setFailed(true)}
          />
        )}
      </div>
      <img
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.18] mix-blend-luminosity"
        src="/logo.svg"
        style={{ left: rect.x + rect.w / 2, top: rect.y + rect.h * 0.47, width: rect.w * 0.58 }}
      />
    </>
  );
}
